/**
 * @brisa/shared/observability/ga
 *
 * Helper canônico para emissão de eventos GA4 (gtag custom event).
 *
 * Convenções:
 *   - No-op silencioso no server (server-side usa logger.info).
 *   - No-op silencioso quando consent não foi concedido (cookie `brisa_consent=granted`)
 *     ou quando GA Measurement ID está ausente.
 *   - `env` é incluído automaticamente em todo evento (ADR-014 — property compartilhada
 *     DEV+PRD com segmentação por param).
 *   - Em DEV/PRD com consent + measurementId presentes, dispara `window.gtag('event', name, props)`.
 *   - Em LOCAL/CI nunca dispara (fail-closed via shouldInjectGA4).
 *
 * Uso (client-side apenas):
 *   import { gaEvent } from '@brisa/shared/observability';
 *   gaEvent('Signup Started', { provider: 'google', user_id: 'anonymous', ... });
 */
import { shouldInjectGA4 } from './analytics';

declare global {
  interface Window {
    gtag?: (
      command: 'event' | 'config' | 'js' | 'set' | 'consent',
      ...args: unknown[]
    ) => void;
    dataLayer?: unknown[];
  }
}

interface EnvSnapshot {
  env: string;
  measurementId: string | undefined | null;
}

function readEnvSnapshot(): EnvSnapshot {
  // No client, lemos NEXT_PUBLIC_* (substituídos no build pelo Next.js).
  // No server, fallback para process.env direto — mas helper é no-op no server de toda forma.
  if (typeof window === 'undefined') {
    return { env: 'server', measurementId: null };
  }
  const env = (process.env.NEXT_PUBLIC_BRISA_ENV as string | undefined) ?? 'local';
  const measurementId =
    (process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID as string | undefined) ?? null;
  return { env, measurementId };
}

function readConsentGranted(): boolean {
  if (typeof document === 'undefined') return false;
  // Cookie banner (a ser construído em features futuras) seta `brisa_consent=granted`.
  // Por enquanto, default-grant em DEV/PRD (sem cookie banner real implementado),
  // mantendo o gate fail-closed em LOCAL/CI via shouldInjectGA4.
  // Quando o cookie banner real existir, esta função será atualizada para ler o cookie.
  const cookies = document.cookie.split(';').map((c) => c.trim());
  const consentCookie = cookies.find((c) => c.startsWith('brisa_consent='));
  if (consentCookie) {
    return consentCookie.endsWith('=granted');
  }
  // Default opt-in em DEV/PRD enquanto banner não existe.
  // Em LOCAL/CI, shouldInjectGA4 já bloqueia por env.
  return true;
}

/**
 * Emite um evento GA4 custom via gtag('event', name, props).
 *
 * No-op se: rodar no server, gate falhar (LOCAL/CI), consent OFF, ou ID ausente.
 * Sempre inclui `env` no payload (ADR-014).
 */
export function gaEvent(name: string, props: Record<string, unknown> = {}): void {
  if (typeof window === 'undefined') return;
  const snapshot = readEnvSnapshot();
  const consentGranted = readConsentGranted();
  if (
    !shouldInjectGA4({
      env: snapshot.env,
      consentGranted,
      measurementId: snapshot.measurementId,
    })
  ) {
    return;
  }
  if (typeof window.gtag !== 'function') return;
  // Inclui env automaticamente (ADR-014 — segmentação por property compartilhada).
  const enrichedProps = { ...props, env: snapshot.env };
  try {
    window.gtag('event', name, enrichedProps);
  } catch {
    // Silenciar — telemetria nunca quebra UX.
  }
}
